//import React from "react";
import { useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { common_utils } from "../../../common/common_utils";
import {
	DiscordAuthToken,
	GetGuildIDFromPollIDRequest,
	GuildData,
	ICacheAccessTokenParams,
	SignUpPresetsProp,
	WebDashboardType,
} from "../../../common/sesh_types";
import "../../css/dashboard.scss";
import { DashboardUtils } from "./dashboard_utils";
import SeshRouter, { redirectToDiscordAuth } from "./sesh_router";
import { SignUpOptionsForm } from "./sign_up_presets";
import { trpc } from "./utils/trpc";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";

// Create root once for the entire app
const rootContainer = document.getElementById("root");
const root = rootContainer ? createRoot(rootContainer) : null;

export function setDocTitle(info: string, guild_name?: string) {
	const strings = [info];
	if (guild_name) {
		strings.push(guild_name);
	}
	strings.push("sesh.fyi");

	document.title = strings.join(" - ");
}

async function onLoad() {
	root?.render(
		<DashboardUtils.RenderFullLoadingThrobberPage />
	);

	const query = new URLSearchParams(window.location.search);
	const is_on_sesh_fyi_production =
		window?.location?.host?.includes("sesh.fyi") &&
		!window?.location?.host?.includes("beta");
	let client_id = is_on_sesh_fyi_production
		? "616754792965865495"
		: await (await fetch(`/api/get_bot_client_id`)).text();

	let access_token: string = null;
	let token_type: string = null;

	let state;
	let state_string: string;
	if (query.has("state")) {
		// quick fix for + being converted to a space in query.get()
		state_string = query.get("state").replace(" ", "+");
		state = JSON.parse(atob(state_string));
	}

	if (query.has("code") && state?.code_source === "discord") {
		localStorage.clear();

		const code = query.get("code");

		// Remove code from URL
		const url = new URL(window.location.href);
		url.search = "";
		window.history.replaceState({}, "", url);

		// Exchange code for token
		const response = await fetch("/api/token", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ code }),
		});

		if (!response.ok) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Failed to exchange code for token"
			);
			return;
		}

		const tokenData = await response.json();
		access_token = tokenData.access_token;
		token_type = tokenData.token_type;

		localStorage.setItem("access_token", access_token);
		localStorage.setItem("token_type", token_type);
	} else {
		access_token = localStorage.getItem("access_token");
		token_type = localStorage.getItem("token_type");
	}

	let redirect_uri: string;

	if (state) {
		switch (state.dashboard_type) {
			case WebDashboardType.Redirect: {
				redirect_uri = state.redirect;
				break;
			}
			case WebDashboardType.Guild_Selection: {
				redirect_uri = "/dashboard";
				break;
			}
			case WebDashboardType.Event: {
				// old behavior: `!create` => creates an event with a link to the channel, but event channel overrides it
				// new behavior: `!create` => creates an event with a link to the channel, but event channel does not override it?
				let new_url: string;
				if (state.event_message_id) {
					new_url = common_utils.getEventEditURL(
						state.guild_id,
						state.event_message_id
					);
				} else {
					new_url = common_utils.getEventCreateURL(
						state.guild_id,
						state.original_channel_id
					);
				}
				redirect_uri = new_url;
				break;
			}
			case WebDashboardType.Poll:
				{
					let new_url: string;
					if (state.message_id) {
						new_url = common_utils.getPollEditURL(
							state.guild_id,
							state.message_id
						);
					} else {
						new_url = common_utils.getPollCreateURL(
							state.guild_id,
							state.original_channel_id
						);
					}
					redirect_uri = new_url;
				}
				break;
			case WebDashboardType.Poll_Results:
				{
					if (access_token && token_type) {
						const poll_id = Boolean(state.poll_id)
							? state.poll_id
							: state.extra_state.poll_id;

						if (!poll_id) {
							DashboardUtils.ShowDefaultErrorAlert(
								"ERROR: No Poll ID Found."
							);
							return;
						}
						const request_body: GetGuildIDFromPollIDRequest = {
							access_token,
							token_type,
							message_id:
								state.poll_id ?? state.extra_state.poll_id,
						};
						const result = await fetch(
							`/api/get_guild_id_from_poll_id`,
							{
								method: "POST",
								headers: {
									"Content-Type": "application/json",
								},
								body: JSON.stringify(request_body),
							}
						);

						const guild_id = (await result.json()).guild_id;
						redirect_uri = common_utils.getPollResultsURL(
							guild_id,
							poll_id
						);
					} else {
						redirect_uri = window.location.toString();
						// no access token
						redirectToDiscordAuth(
							client_id,
							access_token,
							redirect_uri,
							WebDashboardType.Poll_Results,
							{
								poll_id: state.poll_id,
							}
						);
						return;
					}
				}
				break;
			case WebDashboardType.Server_Settings:
				{
					redirect_uri = common_utils.getGuildSettingsURL(
						state.guild_id
					);
				}
				break;
			default:
				DashboardUtils.ShowDefaultErrorAlert("Invalid dashboard type");
				return;
		}
	}

	if (!access_token || !token_type) {
		redirectToDiscordAuth(
			client_id,
			access_token,
			window.location.pathname + window.location.search
		);
		return;
	}

	if (redirect_uri) {
		history.pushState(null, null, redirect_uri);
	}

	const discord_auth_token: DiscordAuthToken = {
		access_token,
		token_type,
	};

	root?.render(
		<App
			discord_auth_token={discord_auth_token}
			sesh_client_id={client_id}
		/>
	);
}

window.onload = () => {
	// clear session cache on refresh
	for (let key of DashboardUtils.SESSION_CACHE_KEYS) {
		window.sessionStorage.removeItem(key);
	}

	onLoad();
};

export async function switchToPresetForm(
	props: SignUpPresetsProp,
	auth_token: DiscordAuthToken
) {
	root?.render(
		<SignUpOptionsForm {...props} auth_token={auth_token} />
	);
}

export default function App(props: {
	discord_auth_token: DiscordAuthToken;
	sesh_client_id: string;
}) {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						onError: (error: any) => {
							const errorMessage =
								error?.message ||
								"An unexpected error occurred";
							// Don't show modal for custom tier errors - handled gracefully in UI
							if (errorMessage.includes("custom tier access")) {
								return;
							}
							DashboardUtils.ShowDefaultErrorAlert(
								errorMessage,
								"API Error"
							);
						},
					},
					mutations: {
						onError: (error: any) => {
							const errorMessage =
								error?.message ||
								"An unexpected error occurred";
							// Don't show modal for custom tier errors - handled gracefully in UI
							if (errorMessage.includes("custom tier access")) {
								return;
							}
							DashboardUtils.ShowDefaultErrorAlert(
								errorMessage,
								"API Error"
							);
						},
					},
				},
			})
	);
	const [trpcClient] = useState(() =>
		trpc.createClient({
			links: [
				httpBatchLink({
					url: "/trpc",
					headers() {
						return {
							access_token: props.discord_auth_token.access_token,
							token_type: props.discord_auth_token.token_type,
						};
					},
				}),
			],
		})
	);

	return (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<BrowserRouter>
					<SeshRouter
						initial_token={props.discord_auth_token}
						sesh_client_id={props.sesh_client_id}
					/>
				</BrowserRouter>
			</QueryClientProvider>
		</trpc.Provider>
	);
}
